/*
================================================================================
 文件 2: src/context/AuthContext.js
 (用于全局管理用户登录状态和信息)
================================================================================
*/
import React, { createContext, useState, useContext, useEffect } from 'react';
import { apiService } from '../services/apiService';

const AuthContext = createContext(null);

const getUserInfoFromToken = (jwtToken) => {
    if (!jwtToken) return null;
    try {
        const payloadBase64 = jwtToken.split('.')[1];
        const decodedJson = atob(payloadBase64);
        const decodedPayload = JSON.parse(decodedJson);
        return {
            id: decodedPayload.nameid,
            username: decodedPayload.unique_name,
            role: decodedPayload.role
        };
    } catch (error) {
        console.error('Token解析失败:', error);
        apiService.TokenManager.clearTokens();
        return null;
    }
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    // 初始化时从localStorage加载Token
    useEffect(() => {
        let isMounted = true;

        const initializeAuth = () => {
            let accessToken = apiService.TokenManager.getAccessToken();

            // 兼容旧版本: 如果有旧的authToken,迁移到新格式
            const legacyToken = localStorage.getItem('authToken');
            if (legacyToken && !accessToken) {
                apiService.TokenManager.setTokens(legacyToken, '');
                localStorage.removeItem('authToken');
                accessToken = legacyToken;
            }

            if (accessToken) {
                const userInfo = getUserInfoFromToken(accessToken);
                if (userInfo && isMounted) {
                    setUser(userInfo);
                    setToken(accessToken);
                }
            }

            if (isMounted) setIsLoading(false);

            if (typeof apiService.initializeSharedState === 'function') {
                apiService.initializeSharedState().catch((error) => {
                    console.warn('Optional API initialization failed:', error);
                });
            }
        };

        initializeAuth();

        return () => {
            isMounted = false;
        };
    }, []);

    // 监听Token变化,更新用户信息
    useEffect(() => {
        if (token) {
            const userInfo = getUserInfoFromToken(token);
            setUser(userInfo);
        } else {
            setUser(null);
        }
    }, [token]);

    const login = async (username, password) => {
        // apiService.login已经会自动保存双Token
        const data = await apiService.login(username, password);

        // 设置Access Token到状态
        if (data.accessToken) {
            setToken(data.accessToken);
        } else if (data.token) {
            // 兼容旧版本响应格式
            setToken(data.token);
            apiService.TokenManager.setTokens(data.token, '');
        }
    };

    const logout = async () => {
        // 先清除本地状态，让 UI 立即响应
        setToken(null);
        setUser(null);

        // 然后在后台或并行调用 API 撤销 Token
        try {
            await apiService.logout();
        } catch (error) {
            console.error('Logout revocation failed:', error);
        }
    };

    const value = { user, token, login, logout, isLoading };

    return (
        <AuthContext.Provider value={value}>
            {!isLoading && children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    return useContext(AuthContext);
};
